import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import type { ShoppingList, ShoppingListItem } from '../types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Sorts shopping lists by createdAt timestamp in descending order (newest first).
 * This ensures consistent sorting behavior across the application.
 *
 * IMPORTANT: This function assumes all lists have numeric createdAt timestamps.
 * For offline-first consistency, these should be client-generated timestamps
 * that are preserved by the backend during sync operations.
 *
 * @param lists - Array of shopping lists to sort
 * @returns New array with lists sorted by creation time (newest first)
 *
 * @see SORTING_AND_TIMESTAMPS.md for detailed explanation of timestamp handling
 */
export const sortListsByCreatedAt = (lists: ShoppingList[]): ShoppingList[] => {
  return [...lists].sort((a, b) => b.createdAt - a.createdAt);
};

/**
 * Sorts shopping list items by createdAt timestamp in descending order (newest first).
 * This ensures consistent sorting behavior across the application.
 *
 * IMPORTANT: This function assumes all items have numeric createdAt timestamps.
 * For offline-first consistency, these should be client-generated timestamps
 * that are preserved by the backend during sync operations.
 *
 * This function handles both temporary IDs (temp-*) and real IDs, ensuring
 * proper sorting regardless of sync status.
 *
 * @param items - Array of shopping list items to sort
 * @returns New array with items sorted by creation time (newest first)
 *
 * @see SORTING_AND_TIMESTAMPS.md for detailed explanation of timestamp handling
 */
export const sortItemsByCreatedAt = (items: ShoppingListItem[]): ShoppingListItem[] => {
  return [...items].sort((a, b) => b.createdAt - a.createdAt);
};
